import { useState, useEffect, useRef, useCallback } from 'react';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { ShieldAlert, Wind, Heart, Timer, RefreshCw, Quote } from 'lucide-react';

const BREATH_PHASES = [
  { name: 'Inspire', duration: 4, color: 'text-blue-500' },
  { name: 'Segure', duration: 7, color: 'text-yellow-500' },
  { name: 'Expire', duration: 8, color: 'text-green-500' },
];

const TIPS = [
  'Beba um copo de agua agora.',
  'Faca 10 respiracoes profundas.',
  'Ligue para alguem que voce confia.',
  'Saia para uma caminhada rapida.',
  'Escreva como esta se sentindo.',
  'Lembre-se: isso vai passar.',
  'Escute uma musica que te acalma.',
  'Faca alongamento por 2 minutos.',
];

export default function SOSPage() {
  const [quote, setQuote] = useState('');
  const [showBreath, setShowBreath] = useState(false);
  const [breathPhase, setBreathPhase] = useState(0);
  const [breathTimer, setBreathTimer] = useState(0);
  const [breathCycles, setBreathCycles] = useState(0);
  const [showCountdown, setShowCountdown] = useState(false);
  const [countdown, setCountdown] = useState(300);
  const breathRef = useRef(null);
  const countdownRef = useRef(null);

  const fetchQuote = useCallback(async () => {
    try {
      const res = await api.get('/quotes/random');
      setQuote(res.data.quote);
    } catch { setQuote('Voce e mais forte do que imagina.'); }
  }, []);

  useEffect(() => { fetchQuote(); }, [fetchQuote]);

  // Breathing exercise logic
  useEffect(() => {
    if (!showBreath) {
      if (breathRef.current) clearInterval(breathRef.current);
      return;
    }
    setBreathPhase(0);
    setBreathTimer(BREATH_PHASES[0].duration);
    setBreathCycles(0);

    let phase = 0;
    let timer = BREATH_PHASES[0].duration;

    breathRef.current = setInterval(() => {
      timer--;
      if (timer <= 0) {
        phase = (phase + 1) % 3;
        timer = BREATH_PHASES[phase].duration;
        if (phase === 0) {
          setBreathCycles(prev => prev + 1);
        }
      }
      setBreathPhase(phase);
      setBreathTimer(timer);
    }, 1000);

    return () => { if (breathRef.current) clearInterval(breathRef.current); };
  }, [showBreath]);

  // Countdown timer
  useEffect(() => {
    if (!showCountdown) {
      if (countdownRef.current) clearInterval(countdownRef.current);
      return;
    }
    setCountdown(300);
    countdownRef.current = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) {
          clearInterval(countdownRef.current);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => { if (countdownRef.current) clearInterval(countdownRef.current); };
  }, [showCountdown]);

  const formatTime = (sec) => `${Math.floor(sec / 60)}:${String(sec % 60).padStart(2, '0')}`;

  const currentPhase = BREATH_PHASES[breathPhase];
  const breathProgress = currentPhase ? ((currentPhase.duration - breathTimer) / currentPhase.duration) * 100 : 0;

  return (
    <div className="space-y-6" data-testid="sos-page">
      <div>
        <h1 className="text-3xl font-heading font-bold tracking-tight">SOS - Apoio</h1>
        <p className="text-muted-foreground text-sm mt-1">Voce nao esta sozinho. Estamos aqui para ajudar.</p>
      </div>

      {/* Emergency Card */}
      <Card className="bg-gradient-to-br from-red-50 to-orange-50 dark:from-red-950/30 dark:to-orange-950/30 border-red-200 dark:border-red-800" data-testid="sos-emergency-card">
        <CardContent className="py-8 text-center">
          <div className="w-20 h-20 rounded-full bg-red-100 dark:bg-red-900/50 flex items-center justify-center mx-auto mb-4 animate-pulse">
            <ShieldAlert className="w-10 h-10 text-red-500" />
          </div>
          <h2 className="text-xl font-heading font-bold mb-2">Sentindo vontade forte?</h2>
          <p className="text-sm text-muted-foreground mb-4 max-w-md mx-auto">
            Isso e temporario. A vontade geralmente passa em 5-15 minutos. Use as ferramentas abaixo para te ajudar.
          </p>
          <div className="flex flex-wrap gap-3 justify-center">
            <Button onClick={() => { setShowBreath(true); setShowCountdown(false); }} className="rounded-full" data-testid="start-breathing-btn">
              <Wind className="w-4 h-4 mr-1" /> Respiracao 4-7-8
            </Button>
            <Button onClick={() => { setShowCountdown(true); setShowBreath(false); }} variant="outline" className="rounded-full" data-testid="start-countdown-btn">
              <Timer className="w-4 h-4 mr-1" /> Timer de 5 min
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Breathing Exercise */}
      {showBreath && (
        <Card className="animate-fade-up" data-testid="breathing-exercise">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="font-heading text-lg flex items-center gap-2">
                <Wind className="w-5 h-5" /> Respiracao 4-7-8
              </CardTitle>
              <Button variant="ghost" size="sm" onClick={() => setShowBreath(false)} data-testid="stop-breathing-btn">Parar</Button>
            </div>
          </CardHeader>
          <CardContent className="text-center py-8">
            <div className="relative w-40 h-40 mx-auto mb-6">
              <div className={`w-full h-full rounded-full border-4 flex items-center justify-center transition-all duration-1000 ${
                breathPhase === 0 ? 'border-blue-400 scale-110 bg-blue-50 dark:bg-blue-950/30' :
                breathPhase === 1 ? 'border-yellow-400 scale-100 bg-yellow-50 dark:bg-yellow-950/30' :
                'border-green-400 scale-90 bg-green-50 dark:bg-green-950/30'
              }`}>
                <div className="text-center">
                  <p className={`text-2xl font-heading font-bold ${currentPhase?.color}`}>{breathTimer}</p>
                  <p className="text-sm font-medium text-muted-foreground">{currentPhase?.name}</p>
                </div>
              </div>
            </div>
            <Progress value={breathProgress} className="h-2 max-w-xs mx-auto mb-3" />
            <p className="text-sm text-muted-foreground">Ciclos completados: {breathCycles}</p>
          </CardContent>
        </Card>
      )}

      {/* Countdown Timer */}
      {showCountdown && (
        <Card className="animate-fade-up" data-testid="countdown-timer">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="font-heading text-lg flex items-center gap-2">
                <Timer className="w-5 h-5" /> Resista so mais um pouco
              </CardTitle>
              <Button variant="ghost" size="sm" onClick={() => setShowCountdown(false)} data-testid="stop-countdown-btn">Parar</Button>
            </div>
          </CardHeader>
          <CardContent className="text-center py-8">
            <p className="text-6xl font-heading font-bold text-primary mb-2">{formatTime(countdown)}</p>
            <Progress value={((300 - countdown) / 300) * 100} className="h-2 max-w-xs mx-auto mb-3" />
            <p className="text-sm text-muted-foreground">
              {countdown === 0 ? 'Voce conseguiu! A vontade passou.' : 'Voce esta indo muito bem. Continue.'}
            </p>
          </CardContent>
        </Card>
      )}

      {/* Motivational Quote */}
      <Card data-testid="motivational-quote-card">
        <CardContent className="py-6 px-6">
          <div className="flex items-start gap-3">
            <Quote className="w-6 h-6 text-primary flex-shrink-0 mt-1" />
            <div className="flex-1">
              <p className="text-lg font-medium italic leading-relaxed">&ldquo;{quote}&rdquo;</p>
              <Button variant="ghost" size="sm" className="mt-3" onClick={fetchQuote} data-testid="new-quote-btn">
                <RefreshCw className="w-3 h-3 mr-1" /> Nova frase
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Quick Tips */}
      <div>
        <h2 className="text-xl font-heading font-semibold mb-3">Dicas Rapidas</h2>
        <div className="grid gap-3 md:grid-cols-2">
          {TIPS.map((tip, i) => (
            <Card key={i} className="hover:shadow-md transition-shadow">
              <CardContent className="py-3 px-4 flex items-center gap-3">
                <Heart className="w-4 h-4 text-primary flex-shrink-0" />
                <p className="text-sm">{tip}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>

      {/* Resources */}
      <Card data-testid="resources-card">
        <CardHeader>
          <CardTitle className="font-heading text-lg">Recursos de Apoio</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="space-y-2">
            <h3 className="text-sm font-semibold">Tecnicas de Controle</h3>
            <ul className="text-sm text-muted-foreground space-y-1.5 pl-4">
              <li>Mindfulness: Foque no momento presente sem julgamento</li>
              <li>CBT: Identifique pensamentos automaticos e desafie-os</li>
              <li>Distracao positiva: Substitua o vicio por atividade prazerosa</li>
              <li>Exercicio fisico: Libera endorfinas naturais</li>
            </ul>
          </div>
          <div className="space-y-2 pt-2">
            <h3 className="text-sm font-semibold">Ligue Agora</h3>
            <p className="text-sm text-muted-foreground">CVV - Centro de Valorizacao da Vida: <span className="font-bold text-primary">188</span></p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
